import { ComponentProps, FC, useMemo } from 'react';

import { Transaction } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { calculateTotalNegativeAmount, calculateTotalOpenAmount, calculateTotalPositiveDueAmount } from '../../../utils/invoice-amount.utils';
import { canSelectInvoice } from '../../../utils/invoice.utils';

type PaymentButtonVariant = 'selected' | 'open' | 'overdue';

type Props = {
   currency: string;
   openInvoices: Transaction[];
   filteredOpenInvoices: Transaction[];
   selectedInvoiceIds: string[];
   canDeductCreditNotes: boolean;
   onPayClick: (type: PaymentButtonVariant) => void;
};

type PaymentButtonProps = {
   variant: PaymentButtonVariant;
   selectedCount?: number;
   amount: number;
   totalAmount?: number;
   currency: string;
   disabled?: boolean;
   disabledMessage?: string;
   onPayClick: (type: PaymentButtonVariant) => void;
};

const paymentButtonTitleConfig: Record<PaymentButtonVariant, string> = {
   selected: 'invoices.invoice-summary.selected-count-title',
   open: 'invoices.invoice-summary.total-amount',
   overdue: 'invoices.invoice-summary.overdue-title'
};

const getPaymentButtonColors = (variant: PaymentButtonVariant, amount: number) => {
   let color: ComponentProps<typeof Typography>['color'];
   if (amount === 0) {
      color = 'weak';
   } else if (variant === 'overdue') {
      color = 'error';
   } else {
      color = 'primary';
   }

   let background: string | undefined;
   if (amount > 0 && variant === 'selected') {
      background = 'var(--theme-color-on-primary-subtle)';
   }

   let borderColor: string | undefined;
   if (amount > 0 && variant === 'selected') {
      borderColor = 'var(--theme-color-primary)';
   }

   return { color, background, borderColor };
};

const getPaymentButtonLabel = (variant: PaymentButtonVariant) => {
   if (variant === 'selected') return 'invoices.invoice-summary.pay-selected';
   if (variant === 'open') return 'invoices.invoice-summary.pay-open';
   return 'invoices.invoice-summary.pay-overdue';
};

const PaymentButton = ({ variant, selectedCount, amount, totalAmount, currency, disabled, disabledMessage, onPayClick }: PaymentButtonProps) => {
   const translate = useTranslations();
   const format = useFormatter();

   const title = translate(paymentButtonTitleConfig[variant], { count: variant === 'selected' ? selectedCount! : '' });
   const { color, background, borderColor } = getPaymentButtonColors(variant, amount);

   const canPay = amount > 0 && !disabled;
   const showTotal = !!totalAmount && totalAmount !== amount;

   const PayButton = (
      <Button
         variant="primary"
         disabled={!canPay}
         leadingIcon={disabled && disabledMessage ? 'circle-info' : undefined}
         ariaLabel={translate(getPaymentButtonLabel(variant))}
         onClick={() => onPayClick(variant)}
      >
         {translate('invoices.invoice-summary.pay')}
      </Button>
   );

   return (
      <Box spacing="md" border borderRadius="largest" items="center" justify="between" background={background} borderColor={borderColor}>
         <Box direction="column" gap="none" grow={1}>
            <Typography color={color} variant="caption">
               {variant === 'selected' && <Icon icon="square-check" size="caption" color="inherit" />} {title}
            </Typography>
            <Box items="center" gap="xs">
               <Typography variant="heading-3" color={color}>
                  {format.price({ amount, currency })}
               </Typography>

               {showTotal && (
                  <Tooltip text={translate('invoices.invoice-summary.invalid-payment-media')}>
                     <Icon icon="circle-question" color="subtle" size="small" />
                  </Tooltip>
               )}
            </Box>
         </Box>
         {disabled && disabledMessage ? <Tooltip text={disabledMessage}>{PayButton}</Tooltip> : PayButton}
      </Box>
   );
};

export const InvoicePaymentSummary: FC<Props> = ({
   currency,
   openInvoices,
   filteredOpenInvoices,
   selectedInvoiceIds,
   canDeductCreditNotes,
   onPayClick
}) => {
   const translate = useTranslations();
   const format = useFormatter();
   const { desktop } = useBreakpoint('desktop');

   const totalSelectedAmount = calculateTotalOpenAmount(openInvoices.filter(({ id }) => selectedInvoiceIds.includes(id)));
   const totals = useMemo(() => {
      const totalAmount = calculateTotalOpenAmount(openInvoices);
      const totalOnlinePayableAmount = calculateTotalOpenAmount(openInvoices.filter(canSelectInvoice));
      const totalOverDue = calculateTotalPositiveDueAmount(openInvoices);
      const totalOnlinePayableOverDue = calculateTotalPositiveDueAmount(openInvoices.filter(canSelectInvoice));
      const totalCredit = calculateTotalNegativeAmount(openInvoices);

      return {
         totalAmount,
         totalOnlinePayableAmount,
         totalOverDue,
         totalOnlinePayableOverDue,
         totalCredit
      };
   }, [openInvoices]);

   const canPaySelected = !canDeductCreditNotes || totalSelectedAmount + totals.totalCredit >= 0;

   return (
      <Box direction="column" width="100%" gap="xs">
         <Box justify="between" items="center" height="45px">
            <Box gap="xs">
               <Typography color="subtle">{translate('invoices.invoice-summary.total-open-amount')}</Typography>
               <Typography variant="body-highlight" color="strong">
                  {format.price({ amount: totals.totalAmount, currency })}
               </Typography>
            </Box>
            <Typography variant="small" color="subtle">
               {translate(`invoices.invoice-summary.number-of-invoices${filteredOpenInvoices.length === 1 ? '-singular' : ''}`, {
                  number: filteredOpenInvoices.length
               })}
            </Typography>
         </Box>
         <Typography variant="body-highlight" color="subtle">
            {translate('invoices.invoice-summary.payment-options')}:
         </Typography>
         <Box direction={desktop ? 'row' : 'column'}>
            <PaymentButton
               variant="selected"
               selectedCount={selectedInvoiceIds.length}
               amount={totalSelectedAmount}
               currency={currency}
               disabled={!canPaySelected}
               disabledMessage={translate('invoices.invoice-summary.pay-disabled-message')}
               onPayClick={onPayClick}
            />
            <PaymentButton
               variant="open"
               amount={totals.totalOnlinePayableAmount}
               currency={currency}
               totalAmount={totals.totalAmount}
               onPayClick={onPayClick}
            />
            <PaymentButton
               variant="overdue"
               amount={totals.totalOnlinePayableOverDue}
               currency={currency}
               totalAmount={totals.totalOverDue}
               onPayClick={onPayClick}
            />
         </Box>
         {canDeductCreditNotes && totals.totalCredit !== 0 && (
            <Typography variant="caption" color="subtle">
               <Icon size="caption" icon="circle-info" color="inherit" /> {translate('invoices.invoice-summary.credit-notes-message')} (
               {format.price({ amount: totals.totalCredit, currency })})
            </Typography>
         )}
      </Box>
   );
};
